/**
 * LeagueJoinPage — `/league/:code` — short, shareable join URL.
 *
 * Flow:
 *   1. Anonymous → render a "join Curvle to take this kick" landing with
 *      the league name (peeked from the server's non-member view), CTA to
 *      sign in. After sign-in we auto-attempt join.
 *   2. Signed-in → call api.joinLeague(code) immediately, then redirect
 *      to /leagues/:id. If the user is already a member, same redirect.
 *   3. Errors → show the specific reason (country-locked, full, not found).
 *
 * The server's joinUrl format is `https://www.curvle.world/league/<code>`,
 * matching this route.
 */
import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ApiError, api } from "@/api/client";
import { useAuthStore } from "@/store/authStore";
import { SignInModal } from "@/components/SignInModal";

export function LeagueJoinPage() {
  const { t } = useTranslation();
  const { code: rawCode = "" } = useParams<{ code: string }>();
  const code = rawCode.toUpperCase();
  const navigate = useNavigate();
  const status = useAuthStore((s) => s.status);
  const user = useAuthStore((s) => s.user);

  const [error, setError] = useState<string | null>(null);
  const [signInOpen, setSignInOpen] = useState(false);
  const [tried, setTried] = useState(false);

  // For signed-in users we attempt the join immediately and redirect.
  // For anonymous users we render a sign-in landing — once they sign in
  // the auth-state effect re-triggers the join attempt.
  //
  // We don't peek the league name pre-join: that would need a separate
  // public endpoint that accepts a code (today's getLeague is by id and
  // requires membership for full info). The CTA copy works without the
  // name; users see the league title immediately after redirecting.
  useEffect(() => {
    if (status !== "authed" || !user?.email || tried) return;
    setTried(true);
    void (async () => {
      try {
        const r = await api.joinLeague(code);
        navigate(`/leagues/${r.league.id}`, { replace: true });
      } catch (err) {
        if (err instanceof ApiError) {
          const body = err.body as { error?: string; countryLock?: string } | undefined;
          if (err.status === 404) setError(t("league_join.invalid_link"));
          else if (err.status === 403 && body?.error === "country_lock") {
            setError(
              t("league_join.country_lock_error", {
                lock: body.countryLock,
                user: user.countryCode,
              }),
            );
          } else if (err.status === 409 && body?.error === "league_full") {
            setError(t("league_join.league_full"));
          } else if (err.status === 401) {
            setSignInOpen(true);
          } else {
            setError(t("league_join.generic_error"));
          }
        } else {
          setError(t("league_join.generic_error"));
        }
      }
    })();
  }, [code, status, user?.email, user?.countryCode, tried, navigate]);

  // Reset tried so the next auth state change retries the join.
  useEffect(() => {
    if (status === "authed") setTried(false);
  }, [status]);

  return (
    <Shell>
      <p className="text-xs font-semibold uppercase tracking-wider opacity-60">
        {t("league_join.kicker")}
      </p>
      <h1 className="mt-1 text-3xl font-bold">
        <span className="font-mono tracking-widest text-amber-200">{code}</span>
      </h1>

      {/* Anonymous view */}
      {(status === "anonymous" || !user?.email) && (
        <div className="mt-5 rounded-2xl bg-white/5 p-5 ring-1 ring-white/10">
          <p className="text-sm opacity-80">
            {t("league_join.anon_body")}
          </p>
          <div className="mt-4 flex flex-wrap gap-2">
            <button
              type="button"
              className="rounded-full bg-amber-300 px-4 py-2 font-semibold text-emerald-950 hover:bg-amber-200"
              onClick={() => setSignInOpen(true)}
            >
              {t("league_join.signin_to_join")}
            </button>
            <Link
              to="/play"
              className="rounded-full bg-white/10 px-4 py-2 font-semibold hover:bg-white/20"
            >
              {t("league_join.kick_first")}
            </Link>
          </div>
        </div>
      )}

      {/* In-progress joining */}
      {status === "authed" && user?.email && !error && (
        <div className="mt-5 rounded-2xl bg-white/5 p-5 ring-1 ring-white/10">
          <p className="text-sm opacity-80">{t("league_join.joining")}</p>
        </div>
      )}

      {/* Error state */}
      {error && (
        <div className="mt-5 rounded-2xl bg-rose-900/30 p-5 ring-1 ring-rose-500/40">
          <p className="text-sm text-rose-100">{error}</p>
          <div className="mt-4 flex flex-wrap gap-2">
            <Link
              to="/leagues"
              className="rounded-full bg-white/10 px-4 py-2 text-sm font-semibold hover:bg-white/20"
            >
              {t("league_join.back_to_leagues")}
            </Link>
            <Link
              to="/play"
              className="rounded-full bg-amber-300 px-4 py-2 text-sm font-semibold text-emerald-950 hover:bg-amber-200"
            >
              {t("league_join.take_kick")}
            </Link>
          </div>
        </div>
      )}

      <SignInModal open={signInOpen} onClose={() => setSignInOpen(false)} />
    </Shell>
  );
}

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-screen bg-emerald-950 text-white">
      <header className="border-b border-white/10 px-4 py-3">
        <div className="mx-auto flex max-w-2xl items-center justify-between">
          <Link to="/" className="text-sm font-bold tracking-wider opacity-80 hover:opacity-100">
            CURVLE
          </Link>
          <Link
            to="/play"
            className="rounded-full bg-white/10 px-3 py-1.5 text-xs font-semibold hover:bg-white/20"
          >
            Today&rsquo;s kick →
          </Link>
        </div>
      </header>
      <main className="mx-auto max-w-2xl px-4 py-6">{children}</main>
    </div>
  );
}
